import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  Barcode,
  Braces,
  CheckCircle2,
  FileText,
  Loader2,
  Mail,
  RotateCcw,
} from 'lucide-react'
import { Toaster, toast } from 'sonner'

import {
  getNfeQrEmailOptions,
  sendNfeQrEmail,
  type NfeQrDocumentType,
  type NfeQrEmailOptions,
} from '@/lib/api'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'

type NfeQrEmailViewProps = {
  companyName?: string | null
  logoUrl?: string | null
  lojaId?: number | null
}

type Destination = NfeQrEmailOptions['destinatarios'][number]

const HASH_PATTERN = /^[a-f0-9]{32}$/i

export const resolveNfeQrHashFromSearch = (search: string) => {
  const hash = new URLSearchParams(search).get('id')?.trim().toLowerCase() ?? ''
  return HASH_PATTERN.test(hash) ? hash : ''
}

export const resolveNfeQrInitialDocument = (
  options: NfeQrEmailOptions,
  search: string,
): NfeQrDocumentType | null => {
  const requested = new URLSearchParams(search).get('documento')
  if (requested && options.documentos.some((document) => document.tipo === requested)) {
    return requested as NfeQrDocumentType
  }
  if (requested === 'danfe_boletos' && options.documentos.some((document) => document.tipo === 'danfe')) {
    return 'danfe'
  }
  return null
}

export const buildNfeQrDestinationKey = (destination: Destination) =>
  `${destination.destino_tipo}:${destination.contato_id ?? 0}`

const documentIcon = (type: NfeQrDocumentType) => {
  if (type === 'xml') return Braces
  if (type === 'boleto') return Barcode
  if (type === 'danfe_boletos') return FileText
  return FileText
}

const errorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const message = error.message
    .replace(/^API error:\s*\d+\s*-?\s*/i, '')
    .trim()
  return message || fallback
}

export function NfeQrEmailView({ companyName, logoUrl, lojaId }: NfeQrEmailViewProps) {
  const [options, setOptions] = useState<NfeQrEmailOptions | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<NfeQrDocumentType | null>(null)
  const [selectedDestinationKey, setSelectedDestinationKey] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sentTo, setSentTo] = useState('')

  const hash = useMemo(() => (
    typeof window === 'undefined' ? '' : resolveNfeQrHashFromSearch(window.location.search)
  ), [])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!hash) {
        setError('Este QR Code é inválido ou está incompleto.')
        setLoading(false)
        return
      }
      if (lojaId && lojaId !== 101) {
        setError('Este serviço está disponível somente nas lojas Brasiltec e Source.')
        setLoading(false)
        return
      }

      setLoading(true)
      setError('')
      try {
        const response = await getNfeQrEmailOptions(hash, lojaId ?? undefined)
        if (!active) return
        setOptions(response)
        setSelectedDocument(resolveNfeQrInitialDocument(response, window.location.search))
      } catch (loadError) {
        if (!active) return
        setError(errorMessage(loadError, 'Não foi possível localizar a nota fiscal deste QR Code.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [hash, lojaId])

  const selectedDestination = options?.destinatarios.find(
    (destination) => buildNfeQrDestinationKey(destination) === selectedDestinationKey,
  ) ?? null

  const handleSend = async () => {
    if (!hash || !selectedDocument || !selectedDestination) return
    setSending(true)
    setError('')
    try {
      const response = await sendNfeQrEmail(hash, {
        documento: selectedDocument,
        destino_tipo: selectedDestination.destino_tipo,
        contato_id: selectedDestination.contato_id,
      }, lojaId ?? undefined)
      setSentTo(response.destino_mascarado)
      toast.success(response.motivo || 'Documento enviado por e-mail.')
    } catch (sendError) {
      const message = errorMessage(sendError, 'Não foi possível enviar o documento. Tente novamente.')
      setError(message)
      toast.error(message)
    } finally {
      setSending(false)
    }
  }

  const resetSelection = () => {
    setSentTo('')
    setSelectedDocument(null)
    setSelectedDestinationKey('')
    setError('')
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-xl">
        <div className="mb-6 flex min-h-14 items-center justify-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName ? `Logo ${companyName}` : 'Logo da loja'}
              className="max-h-14 max-w-[220px] object-contain"
            />
          ) : (
            <span className="text-xl font-bold tracking-tight text-slate-900">
              {companyName || 'Nota fiscal'}
            </span>
          )}
        </div>

        <Card className="overflow-hidden border-slate-200 shadow-xl shadow-slate-200/50">
          <CardHeader className="border-b border-slate-100 bg-white text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-700">
              <Mail className="h-6 w-6" aria-hidden="true" />
            </div>
            <CardTitle className="text-2xl text-slate-950">Documentos da nota fiscal</CardTitle>
            <CardDescription className="text-base">
              {options?.numero ? `NF-e nº ${options.numero}` : 'Consulte os documentos vinculados ao QR Code.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6 p-5 sm:p-7">
            {loading ? (
              <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-slate-600">
                <Loader2 className="h-8 w-8 animate-spin text-blue-700" aria-hidden="true" />
                <p>Localizando a nota fiscal...</p>
              </div>
            ) : sentTo ? (
              <div className="flex min-h-56 flex-col items-center justify-center text-center">
                <CheckCircle2 className="mb-4 h-14 w-14 text-emerald-600" aria-hidden="true" />
                <h2 className="text-xl font-bold text-slate-950">Documento enviado</h2>
                <p className="mt-2 max-w-sm text-slate-600">
                  O documento foi enviado para <strong className="font-semibold text-slate-900">{sentTo}</strong>.
                </p>
                <Button type="button" variant="outline" className="mt-6" onClick={resetSelection}>
                  <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Solicitar outro documento
                </Button>
              </div>
            ) : options ? (
              <>
                {options.cancelada ? (
                  <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                    <p>Esta NF-e está cancelada. O DANFE será enviado com a identificação de cancelamento.</p>
                  </div>
                ) : null}

                <section aria-labelledby="document-title">
                  <h2 id="document-title" className="text-base font-semibold text-slate-950">
                    1. Escolha o documento
                  </h2>
                  <div className="mt-3 grid gap-3">
                    {options.documentos.map((document) => {
                      const Icon = documentIcon(document.tipo)
                      const selected = selectedDocument === document.tipo
                      return (
                        <button
                          key={document.tipo}
                          type="button"
                          className={`flex w-full items-center gap-4 rounded-xl border px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
                            selected
                              ? 'border-blue-600 bg-blue-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          aria-pressed={selected}
                          onClick={() => {
                            setSelectedDocument(document.tipo)
                            setSentTo('')
                            setError('')
                          }}
                        >
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700'}`}>
                            <Icon className="h-5 w-5" aria-hidden="true" />
                          </span>
                          <span>
                            <span className="block font-semibold text-slate-950">{document.titulo}</span>
                            <span className="mt-0.5 block text-sm text-slate-600">{document.descricao}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </section>

                {selectedDocument ? (
                  <section aria-labelledby="destination-title">
                    <h2 id="destination-title" className="text-base font-semibold text-slate-950">
                      2. Escolha o e-mail cadastrado
                    </h2>
                    {options.destinatarios.length > 0 ? (
                      <div className="mt-3 grid gap-2">
                        {options.destinatarios.map((destination) => {
                          const key = buildNfeQrDestinationKey(destination)
                          const selected = selectedDestinationKey === key
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
                                selected ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                              }`}
                              aria-pressed={selected}
                              onClick={() => {
                                setSelectedDestinationKey(key)
                                setError('')
                              }}
                            >
                              <Mail className={`h-5 w-5 ${selected ? 'text-blue-700' : 'text-slate-500'}`} aria-hidden="true" />
                              <span className="font-medium text-slate-900">{destination.email_mascarado}</span>
                            </button>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                        {options.motivo || 'A nota fiscal não possui e-mail cadastrado para recebimento.'}
                      </p>
                    )}
                  </section>
                ) : null}

                {error ? (
                  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                  </div>
                ) : null}

                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  disabled={!selectedDocument || !selectedDestination || sending || !options.pode_enviar}
                  onClick={() => void handleSend()}
                >
                  {sending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" /> : <Mail className="mr-2 h-5 w-5" aria-hidden="true" />}
                  {sending ? 'Enviando...' : 'Enviar por e-mail'}
                </Button>
              </>
            ) : (
              <div className="flex min-h-48 flex-col items-center justify-center text-center">
                <AlertTriangle className="mb-3 h-12 w-12 text-amber-500" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-slate-950">Documento indisponível</h2>
                <p role="alert" className="mt-2 max-w-sm text-slate-600">
                  {error || 'Não foi possível localizar os documentos desta nota fiscal.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-5 text-center text-xs leading-5 text-slate-500">
          Por segurança, os documentos são enviados somente para e-mails já cadastrados.
        </p>
      </div>
      <Toaster position="bottom-center" />
    </main>
  )
}

